/**
 * Office-style ribbon.
 *
 * Commands that are not built yet are rendered disabled with a tooltip saying
 * so, rather than omitted or wired to a no-op. A greyed button with an honest
 * reason is more useful to a pilot user than a button that silently does
 * nothing -- and it keeps the roadmap visible in the product itself.
 */

import { useState } from 'react'

export interface Command {
  id: string
  label: string
  icon: string
  onClick?: () => void
  disabled?: boolean
  /** Shown when the command is unavailable; explains why. */
  pending?: string
  small?: boolean
  /** Toggled-on state, for tools that stay armed until used. */
  active?: boolean
  /** Tooltip, when the label alone does not explain the command. */
  hint?: string
}

export interface Group {
  label: string
  commands: Command[]
}

export interface Tab {
  id: string
  label: string
  groups: Group[]
}

interface Props {
  tabs: Tab[]
}

export function Ribbon({ tabs }: Props) {
  const [active, setActive] = useState(tabs[0]?.id)
  const current = tabs.find((tab) => tab.id === active) ?? tabs[0]

  return (
    <div className="ribbon">
      <div className="ribbon__tabs" role="tablist" aria-label="Ribbon">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className="ribbon__tab"
            role="tab"
            aria-selected={tab.id === active}
            aria-controls={`ribbon-panel-${tab.id}`}
            onClick={() => setActive(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div
        className="ribbon__panel"
        role="tabpanel"
        id={`ribbon-panel-${current?.id}`}
      >
        {current?.groups.map((group) => (
          <div className="group" key={group.label}>
            <div className="group__items">
              {layout(group.commands).map((item) =>
                Array.isArray(item) ? (
                  <div className="rbtn-stack" key={item.map((c) => c.id).join('+')}>
                    {item.map((command) => <RibbonButton key={command.id} command={command} />)}
                  </div>
                ) : (
                  <RibbonButton key={item.id} command={item} />
                ),
              )}
            </div>
            <div className="group__label">{group.label}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Large buttons in order, then the small ones stacked three high, as in
 * Office: side by side, the small buttons made the ribbon wider than a
 * laptop screen. */
function layout(commands: Command[]): (Command | Command[])[] {
  const large = commands.filter((c) => !c.small)
  const small = commands.filter((c) => c.small)
  const stacks: Command[][] = []
  for (let i = 0; i < small.length; i += 3) stacks.push(small.slice(i, i + 3))
  return [...large, ...stacks]
}

function RibbonButton({ command }: { command: Command }) {
  const unavailable = command.disabled || Boolean(command.pending)
  const title = command.pending
    ? `${command.label} — not implemented yet: ${command.pending}`
    : command.hint ?? command.label

  return (
    <button
      className={command.small ? 'rbtn rbtn--small' : 'rbtn'}
      onClick={command.onClick}
      disabled={unavailable}
      title={title}
      aria-disabled={unavailable}
      aria-pressed={command.active}
      data-active={command.active || undefined}
    >
      <span className="rbtn__icon" aria-hidden="true">
        {command.icon}
      </span>
      <span className="rbtn__label">{command.label}</span>
    </button>
  )
}
